import React, { useRef, useCallback, useState } from 'react'
import { ArrowUpRight, ArrowDownRight, Layers, BarChart2, Shield, Zap, Sparkles } from 'lucide-react'
import { LANDING_PAGE_CONFIG, COLORS } from '../../config/constants'

interface HeroCardProps {
    symbol: string
}

export const HeroCard = React.memo(function HeroCard({ symbol }: HeroCardProps) {
    const cardRef = useRef<HTMLDivElement>(null)
    const [tilt, setTilt] = useState({ rX: 0, rY: 0, sX: 50, sY: 50 })
    const [hovering, setHovering] = useState(false)
    const [activeView, setActiveView] = useState<'ladder' | 'radar' | 'hud'>('ladder')

    const simData =
        (LANDING_PAGE_CONFIG.SIMULATION as Record<string, any>)[symbol] ||
        LANDING_PAGE_CONFIG.SIMULATION.NIFTY!

    const onMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const el = cardRef.current
        if (!el) return
        const r = el.getBoundingClientRect()
        const x = e.clientX - r.left
        const y = e.clientY - r.top
        setTilt({
            rX: ((y - r.height / 2) / (r.height / 2)) * -5,
            rY: ((x - r.width / 2) / (r.width / 2)) * 5,
            sX: (x / r.width) * 100,
            sY: (y / r.height) * 100,
        })
    }, [])

    return (
        <div
            className="relative mt-8 lg:mt-0 max-w-[620px] mx-auto w-full transition-transform duration-300"
            style={{ perspective: '1200px' }}
        >
            {/* Ambient subtle backlighting glow */}
            <div
                className="absolute -inset-4 md:-inset-8 pointer-events-none rounded-3xl opacity-60 transition-opacity duration-700"
                style={{
                    background:
                        'radial-gradient(circle at 50% 40%, rgba(0,84,81,0.18), rgba(16,185,129,0.08) 50%, transparent 75%)',
                    filter: 'blur(45px)',
                }}
            />

            {/* Main Ultra-Premium Terminal Container */}
            <div
                ref={cardRef}
                className="relative rounded-3xl p-1 bg-gradient-to-b from-slate-200/90 via-slate-100/60 to-slate-200/90 dark:from-white/15 dark:via-white/5 dark:to-white/10 shadow-2xl shadow-slate-900/10 dark:shadow-black/70 border border-slate-200/90 dark:border-white/10 transition-all duration-300"
                style={{
                    transform: `rotateX(${tilt.rX}deg) rotateY(${tilt.rY}deg) scale(${hovering ? 1.01 : 1})`,
                    transformStyle: 'preserve-3d',
                }}
                onMouseMove={onMove}
                onMouseEnter={() => setHovering(true)}
                onMouseLeave={() => {
                    setHovering(false)
                    setTilt({ rX: 0, rY: 0, sX: 50, sY: 50 })
                }}
            >
                <div className="rounded-[22px] bg-white/95 dark:bg-[#0c1414]/95 backdrop-blur-xl p-5 sm:p-6 border border-white/80 dark:border-white/10 overflow-hidden relative">
                    {/* Top Terminal Chrome Window Bar */}
                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-4 mb-4">
                        <div className="flex items-center gap-2.5">
                            <div className="flex items-center gap-1.5">
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                            </div>
                            <span className="text-[11px] font-mono font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase ml-1">
                                TERMINAL // {simData.symbol}
                            </span>
                        </div>

                        {/* Live Status Pill */}
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 text-[10px] font-extrabold tracking-wider uppercase font-mono">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                            </span>
                            LIVE STREAM • 2s
                        </div>
                    </div>

                    {/* Spot Header & Key Numbers */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                        <div>
                            <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest font-mono">
                                INDEX SPOT PRICE
                            </div>
                            <div className="flex items-baseline gap-2 mt-0.5">
                                <span className="text-3xl sm:text-4xl font-black font-mono tracking-tight text-slate-900 dark:text-white">
                                    {simData.spotPrice}
                                </span>
                                <span
                                    className={`inline-flex items-center text-xs font-mono font-extrabold px-2 py-0.5 rounded-md ${
                                        simData.isPositive
                                            ? 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800'
                                            : 'text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-800'
                                    }`}
                                >
                                    {simData.isPositive ? (
                                        <ArrowUpRight size={13} className="mr-0.5" />
                                    ) : (
                                        <ArrowDownRight size={13} className="mr-0.5" />
                                    )}
                                    {simData.spotChange} ({simData.spotChangePct})
                                </span>
                            </div>
                        </div>

                        {/* ATM & Bias Badges */}
                        <div className="flex items-center gap-2">
                            <div className="px-3 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-white/10 text-right">
                                <div className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                                    ATM STRIKE
                                </div>
                                <div className="text-xs font-mono font-black text-slate-800 dark:text-slate-200">
                                    {simData.atmStrike}
                                </div>
                            </div>
                            <div className="px-3 py-1.5 rounded-xl bg-emerald-50/80 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-right">
                                <div className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
                                    SENTIMENT
                                </div>
                                <div className="text-xs font-mono font-black text-emerald-800 dark:text-emerald-300">
                                    {simData.sentiment}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cockpit View Switcher Tabs */}
                    <div className="flex items-center gap-1.5 bg-slate-100/90 dark:bg-slate-900/90 p-1 rounded-xl mb-4 text-xs font-bold text-slate-600 dark:text-slate-400">
                        <button
                            type="button"
                            onClick={() => setActiveView('ladder')}
                            className={`flex-1 py-1.5 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                                activeView === 'ladder'
                                    ? 'bg-white dark:bg-[#005451] text-slate-900 dark:text-white shadow-xs font-black'
                                    : 'hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Layers size={14} className={activeView === 'ladder' ? 'text-[#005451] dark:text-teal-300' : ''} />
                            Strategy Ladder
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView('radar')}
                            className={`flex-1 py-1.5 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                                activeView === 'radar'
                                    ? 'bg-white dark:bg-[#005451] text-slate-900 dark:text-white shadow-xs font-black'
                                    : 'hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <BarChart2 size={14} className={activeView === 'radar' ? 'text-[#005451] dark:text-teal-300' : ''} />
                            OI Concentration
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveView('hud')}
                            className={`flex-1 py-1.5 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all ${
                                activeView === 'hud'
                                    ? 'bg-white dark:bg-[#005451] text-slate-900 dark:text-white shadow-xs font-black'
                                    : 'hover:text-slate-900 dark:hover:text-white'
                            }`}
                        >
                            <Zap size={14} className={activeView === 'hud' ? 'text-[#005451] dark:text-teal-300' : ''} />
                            Algo Levels
                        </button>
                    </div>

                    {/* View 1: Strategy Chain Ladder */}
                    {activeView === 'ladder' && (
                        <div className="space-y-1.5 font-mono text-[11px] mb-4">
                            {/* Ladder Header */}
                            <div className="grid grid-cols-5 text-center text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500 py-1.5 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-slate-100 dark:border-white/5">
                                <div>CE OI</div>
                                <div>CE CHG</div>
                                <div className="text-slate-700 dark:text-slate-300">STRIKE</div>
                                <div>PE CHG</div>
                                <div>PE OI</div>
                            </div>

                            {/* Strike Rows */}
                            {simData.strikes.map((row: any) => (
                                <div
                                    key={row.strike}
                                    className={`grid grid-cols-5 text-center py-2 px-1.5 rounded-lg border items-center transition-all ${
                                        row.isAtm
                                            ? 'bg-[#005451]/10 dark:bg-[#005451]/25 border-[#005451]/30 dark:border-[#005451]/60 font-black shadow-xs'
                                            : row.isResistance
                                            ? 'bg-rose-50/70 dark:bg-rose-950/40 border-rose-200/90 dark:border-rose-900/40 text-rose-900 dark:text-rose-200'
                                            : row.isSupport
                                            ? 'bg-emerald-50/70 dark:bg-emerald-950/40 border-emerald-200/90 dark:border-emerald-900/40 text-emerald-900 dark:text-emerald-200'
                                            : 'bg-white dark:bg-slate-900/40 border-slate-100 dark:border-white/5 text-slate-700 dark:text-slate-300 hover:border-slate-200 dark:hover:border-white/10'
                                    }`}
                                >
                                    <div className="font-bold text-slate-700 dark:text-slate-300">{row.ceOi}</div>
                                    <div
                                        className={`font-semibold ${
                                            row.ceChg.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                                        }`}
                                    >
                                        {row.ceChg}
                                    </div>
                                    <div className="flex items-center justify-center gap-1 font-black text-slate-900 dark:text-white">
                                        <span>{row.strike}</span>
                                        {row.isAtm && (
                                            <span className="text-[8px] bg-[#005451] text-white px-1 py-0.5 rounded font-mono font-bold leading-none">
                                                ATM
                                            </span>
                                        )}
                                        {row.isResistance && (
                                            <span
                                                className="text-[8px] text-white px-1 py-0.5 rounded font-mono font-bold leading-none"
                                                style={{ backgroundColor: COLORS.RESISTANCE }}
                                            >
                                                RES
                                            </span>
                                        )}
                                        {row.isSupport && (
                                            <span
                                                className="text-[8px] text-white px-1 py-0.5 rounded font-mono font-bold leading-none"
                                                style={{ backgroundColor: COLORS.SUPPORT }}
                                            >
                                                SUP
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className={`font-semibold ${
                                            row.peChg.startsWith('+') ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
                                        }`}
                                    >
                                        {row.peChg}
                                    </div>
                                    <div className="font-bold text-slate-700 dark:text-slate-300">{row.peOi}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* View 2: OI Concentration Radar */}
                    {activeView === 'radar' && (
                        <div className="p-4 rounded-xl bg-slate-50/70 dark:bg-slate-900/60 border border-slate-200/80 dark:border-white/10 mb-4 space-y-3 font-mono">
                            <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400 font-bold">
                                <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-400">
                                    <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> CALL OI WALL
                                </span>
                                <span className="flex items-center gap-1.5 text-rose-700 dark:text-rose-400">
                                    <span className="w-2.5 h-2.5 rounded-sm bg-rose-500" /> PUT OI WALL
                                </span>
                            </div>

                            <div className="space-y-2 pt-1">
                                {simData.strikes.slice(1, 6).map((stk: any) => (
                                    <div key={stk.strike} className="space-y-1">
                                        <div className="flex justify-between text-[10px] text-slate-600 dark:text-slate-400 font-bold">
                                            <span>{stk.ceOi}</span>
                                            <span className="text-slate-900 dark:text-white font-black">{stk.strike}</span>
                                            <span>{stk.peOi}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1.5 h-2">
                                            {/* Left CE bar */}
                                            <div className="flex justify-end">
                                                <div
                                                    className="h-full rounded-sm bg-emerald-500 transition-all duration-500"
                                                    style={{ width: `${Math.min(parseFloat(stk.ceOi) * 2.8, 100)}%` }}
                                                />
                                            </div>
                                            {/* Right PE bar */}
                                            <div className="flex justify-start">
                                                <div
                                                    className="h-full rounded-sm bg-rose-500 transition-all duration-500"
                                                    style={{ width: `${Math.min(parseFloat(stk.peOi) * 2.8, 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* View 3: Algo Levels HUD */}
                    {activeView === 'hud' && (
                        <div className="p-4 rounded-xl bg-slate-50/70 dark:bg-slate-900/60 border border-slate-200/80 dark:border-white/10 mb-4 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 rounded-lg bg-white dark:bg-slate-900/80 border border-rose-200 dark:border-rose-900/40">
                                    <div className="text-[10px] font-bold text-rose-600 dark:text-rose-400 uppercase font-mono">
                                        RESISTANCE STRIKE (TOP 3 MIN)
                                    </div>
                                    <div className="text-xl font-black font-mono text-rose-700 dark:text-rose-300 mt-0.5">
                                        {simData.resistanceStrike}
                                    </div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                                        Heavy CE Call Writing Zone
                                    </div>
                                </div>
                                <div className="p-3 rounded-lg bg-white dark:bg-slate-900/80 border border-emerald-200 dark:border-emerald-900/40">
                                    <div className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase font-mono">
                                        SUPPORT STRIKE (TOP 3 MAX)
                                    </div>
                                    <div className="text-xl font-black font-mono text-emerald-700 dark:text-emerald-300 mt-0.5">
                                        {simData.supportStrike}
                                    </div>
                                    <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                                        Solid PE Put Accumulation Wall
                                    </div>
                                </div>
                            </div>
                            <div className="p-3 rounded-lg bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/10 flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] font-bold text-[#005451] dark:text-teal-400 uppercase font-mono">
                                        DYNAMIC SHIFT = (R + S) / 2
                                    </div>
                                    <div className="text-lg font-black font-mono text-slate-900 dark:text-white mt-0.5">
                                        {simData.shift}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase font-mono">
                                        PCR RATIO
                                    </div>
                                    <div className="text-lg font-black font-mono text-emerald-600 dark:text-emerald-400 mt-0.5">
                                        {simData.pcr}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Institutional Key Levels Ribbon */}
                    <div className="grid grid-cols-3 gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-900/70 border border-slate-200/90 dark:border-white/10 font-mono">
                        <div className="text-center">
                            <div className="text-[9px] font-bold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                                Resistance
                            </div>
                            <div
                                className="text-sm sm:text-base font-black"
                                style={{ color: COLORS.RESISTANCE }}
                            >
                                {simData.resistanceStrike}
                            </div>
                        </div>
                        <div className="text-center border-x border-slate-200 dark:border-white/10">
                            <div className="text-[9px] font-bold uppercase tracking-wider text-[#005451] dark:text-teal-400">
                                Shift Level
                            </div>
                            <div className="text-sm sm:text-base font-black text-[#005451] dark:text-teal-300">
                                {simData.shift}
                            </div>
                        </div>
                        <div className="text-center">
                            <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                                Support
                            </div>
                            <div
                                className="text-sm sm:text-base font-black"
                                style={{ color: COLORS.SUPPORT }}
                            >
                                {simData.supportStrike}
                            </div>
                        </div>
                    </div>

                    {/* Bottom Telemetry Strip */}
                    <div className="mt-3.5 pt-3 border-t border-slate-100 dark:border-white/10 flex items-center justify-between text-[10px] font-mono font-medium text-slate-400 dark:text-slate-500">
                        <span>STRIKE GAP: {simData.strikeStep} PTS</span>
                        <span className="hidden sm:inline">INTRADAY BACKUP: ACTIVE</span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                            <Sparkles size={11} /> READY FOR TRADING
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
})
